// Native <dialog> wrapper: handles Esc + backdrop click + focus trap for
// free; iframe src is set lazily on first open so we don't burn an authorize
// call until the user clicks the CTA. Closing tears the iframe down so a
// re-open starts a fresh PKCE state. Mirrors the worker's ui/modal.tsx
// (HubModalDialog + HubModalScript) ergonomics.

import { useEffect, useRef } from "react";
import { ensureModalStylesheet } from "./modal";

export interface HubModalDialogProps {
  /** The real B2C custom-domain host the iframe loads, e.g. `id.orgbx.com`. */
  b2cHost: string;
  iframeSrc: string;
  iframeTitle: string;
  open: boolean;
  onClose: () => void;
}

export function HubModalDialog(props: HubModalDialogProps) {
  const { b2cHost, iframeSrc, iframeTitle, open, onClose } = props;
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  useEffect(() => {
    ensureModalStylesheet();
  }, []);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open) {
      // Lazy-load iframe src on first open. Re-open (after close) re-sets
      // src to force a fresh PKCE state — symmetric with the worker script.
      if (iframeRef.current && !iframeRef.current.src) {
        iframeRef.current.src = iframeSrc;
      }
      if (typeof dialog.showModal === "function" && !dialog.open) {
        dialog.showModal();
      }
    } else if (dialog.open) {
      dialog.close();
      if (iframeRef.current) iframeRef.current.removeAttribute("src");
    }
  }, [open, iframeSrc]);

  // Native <dialog> emits a click on itself when the backdrop is clicked
  // (target === dialog, not a child). Esc is handled by the dialog itself
  // and surfaces via the `cancel` event.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    function onBackdropClick(ev: MouseEvent) {
      if (ev.target === dialog) onClose();
    }
    function onCancel(ev: Event) {
      ev.preventDefault();
      onClose();
    }
    dialog.addEventListener("click", onBackdropClick);
    dialog.addEventListener("cancel", onCancel);
    return () => {
      dialog.removeEventListener("click", onBackdropClick);
      dialog.removeEventListener("cancel", onCancel);
    };
  }, [onClose]);

  // The B2C iframe can postMessage { hubClose: true } on explicit cancel.
  useEffect(() => {
    function onMessage(ev: MessageEvent) {
      if (ev.data && (ev.data as { hubClose?: boolean }).hubClose === true) {
        onClose();
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [onClose]);

  return (
    <dialog ref={dialogRef} className="hub-modal" aria-labelledby="hub-modal-title">
      <div className="hub-modal-head">
        <span id="hub-modal-title">
          Hosted on <code>{b2cHost}</code>
        </span>
        <button
          type="button"
          className="hub-modal-close"
          aria-label="Close"
          onClick={onClose}
        >
          ×
        </button>
      </div>
      <iframe
        ref={iframeRef}
        className="hub-modal-frame"
        title={iframeTitle}
        sandbox="allow-scripts allow-forms allow-same-origin allow-top-navigation allow-popups"
      />
    </dialog>
  );
}
